import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Typography, Accordion, AccordionSummary, AccordionDetails, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { apiService } from '../../services/api';

/**
 * Diagnóstico de un equipo: por qué dejó de funcionar y qué hacer.
 *
 * No se carga solo. Cruzar Traccar, SimBase y el portal son varias llamadas a
 * APIs externas y tarda unos segundos, así que se pide cuando alguien está
 * atendiendo un soporte, no cada vez que se abre una ficha.
 */

const COLOR_GRAVEDAD: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  critico: 'error',
  alto: 'error',
  medio: 'warning',
  bajo: 'info',
  ok: 'success',
};

const TEXTO_GRAVEDAD: Record<string, string> = {
  critico: 'Crítico',
  alto: 'Grave',
  medio: 'A revisar',
  bajo: 'Menor',
  ok: 'Todo bien',
};

const COLOR_FUENTE: Record<string, string> = {
  traccar: '#1565c0',
  simbase: '#6a1b9a',
  energia: '#c62828',
};

const NOMBRE_FUENTE: Record<string, string> = {
  traccar: 'Traccar',
  simbase: 'SimBase',
  energia: 'Energía',
};

interface Props {
  deviceId: string;
}

/**
 * Curva de descarga. Es la evidencia que convierte "dejó de reportar" en "se
 * quedó sin corriente y se fue agotando": se ve el voltaje del vehículo caer a
 * cero y, a partir de ahí, la batería interna bajando hasta que el equipo calla.
 */
const GraficaEnergia: React.FC<{ serie: any[] }> = ({ serie }) => {
  if (!serie || serie.length < 2) return null;

  const puntos = serie.filter((p) => p.bat != null || p.ext != null);
  if (puntos.length < 2) return null;

  const w = 720;
  const h = 180;
  const padL = 34;
  const padR = 34;
  const padY = 14;

  const t0 = new Date(puntos[0].t).getTime();
  const t1 = new Date(puntos[puntos.length - 1].t).getTime();
  const span = Math.max(t1 - t0, 1);
  const x = (t: string) => padL + ((new Date(t).getTime() - t0) / span) * (w - padL - padR);

  // Dos escalas: el vehículo ronda 12–14 V y el respaldo 3–4,2 V. Compartir eje
  // aplastaría la curva del respaldo, que es justo la que importa.
  const yExt = (v: number) => padY + (1 - Math.min(v, 16) / 16) * (h - padY * 2);
  const yBat = (v: number) => padY + (1 - (Math.min(Math.max(v, 3), 4.3) - 3) / 1.3) * (h - padY * 2);

  const linea = (campo: 'ext' | 'bat', esc: (v: number) => number) => {
    const trozos: string[] = [];
    let abierto = false;
    for (const p of puntos) {
      const v = p[campo];
      if (v == null) { abierto = false; continue; }
      trozos.push(`${abierto ? 'L' : 'M'}${x(p.t).toFixed(1)},${esc(v).toFixed(1)}`);
      abierto = true;
    }
    return trozos.join(' ');
  };

  const cortes: Array<{ x1: number; x2: number }> = [];
  let inicio: string | null = null;
  for (const p of puntos) {
    if (p.ext != null && p.ext < 1 && !inicio) inicio = p.t;
    if (p.ext != null && p.ext >= 1 && inicio) { cortes.push({ x1: x(inicio), x2: x(p.t) }); inicio = null; }
  }
  if (inicio) cortes.push({ x1: x(inicio), x2: w - padR });

  const hora = (t: string) =>
    new Date(t).toLocaleString('es-DO', { timeZone: 'America/Santo_Domingo', day: 'numeric', month: 'short', hour: '2-digit' });

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${w} ${h + 18}`} style={{ width: '100%', minWidth: 520 }} role="img"
        aria-label="Voltaje del vehículo y de la batería interna a lo largo del tiempo">
        {cortes.map((c, i) => (
          <rect key={i} x={c.x1} y={padY} width={Math.max(c.x2 - c.x1, 1)} height={h - padY * 2}
            fill="#c62828" opacity="0.09" />
        ))}
        <line x1={padL} y1={h - padY} x2={w - padR} y2={h - padY} stroke="currentColor" opacity="0.2" />
        <path d={linea('ext', yExt)} fill="none" stroke="#1565c0" strokeWidth="1.6" />
        <path d={linea('bat', yBat)} fill="none" stroke="#ef6c00" strokeWidth="1.6" />
        <text x={padL} y={h + 13} fontSize="10" opacity="0.65">{hora(puntos[0].t)}</text>
        <text x={w - padR} y={h + 13} fontSize="10" opacity="0.65" textAnchor="end">
          {hora(puntos[puntos.length - 1].t)}
        </text>
      </svg>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#1565c0' }}>── Corriente del vehículo</Typography>
        <Typography variant="caption" sx={{ color: '#ef6c00' }}>── Batería interna del equipo</Typography>
        <Typography variant="caption" sx={{ color: '#c62828' }}>▨ Sin corriente</Typography>
      </Box>
    </Box>
  );
};

const DiagnosticoDispositivo: React.FC<Props> = ({ deviceId }) => {
  const [informe, setInforme] = useState<any>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dias, setDias] = useState(5);
  const [ejecutando, setEjecutando] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ ok: boolean; mensaje: string } | null>(null);
  const [confirmar, setConfirmar] = useState<{ clave: string; etiqueta: string; descripcion?: string } | null>(null);

  const correr = async (d = dias) => {
    setCargando(true);
    setError(null);
    setResultado(null);
    try {
      const res = await apiService.getDeviceDiagnostico(deviceId, d);
      setInforme(res?.data || res);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'No se pudo generar el diagnóstico');
    }
    setCargando(false);
  };

  const ejecutar = async (clave: string) => {
    setEjecutando(clave);
    setResultado(null);
    try {
      const res: any = await apiService.ejecutarAccionDiagnostico(deviceId, clave);
      setResultado({ ok: res?.success !== false, mensaje: res?.mensaje || 'Listo' });
      // Tras actuar sobre la SIM el informe anterior ya no refleja la realidad.
      await correr();
    } catch (e: any) {
      setResultado({ ok: false, mensaje: e?.response?.data?.message || e.message || 'Falló la acción' });
    }
    setEjecutando(null);
    setConfirmar(null);
  };

  const principal = informe?.hallazgos?.[0];

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Diagnóstico y reparación</Typography>
            <Typography variant="body2" color="text.secondary">
              Cruza Traccar, SimBase y el portal para decir por qué dejó de funcionar.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[2, 5, 15].map((d) => (
              <Button key={d} size="small" variant={dias === d ? 'contained' : 'outlined'}
                onClick={() => { setDias(d); if (informe) correr(d); }} disabled={cargando}>
                {d} días
              </Button>
            ))}
            <Button variant="contained" onClick={() => correr()} disabled={cargando}>
              {cargando ? <CircularProgress size={20} /> : informe ? 'Actualizar' : 'Diagnosticar'}
            </Button>
          </Box>
        </Box>

        {!informe && !cargando && !error && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No se ejecuta solo para no cargar la aplicación. Dale a <strong>Diagnosticar</strong> cuando estés
            atendiendo un soporte de este equipo.
          </Alert>
        )}

        {cargando && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Consultando Traccar y SimBase…
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {resultado && (
          <Alert severity={resultado.ok ? 'success' : 'error'} sx={{ mt: 2 }} onClose={() => setResultado(null)}>
            {resultado.mensaje}
          </Alert>
        )}

        {informe && (
          <Box sx={{ mt: 2 }}>
            {informe.fuentesCaidas?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No respondieron: {informe.fuentesCaidas.map((f: any) => `${f.fuente} (${f.error})`).join(', ')}.
                El diagnóstico se hizo con lo que sí llegó.
              </Alert>
            )}

            {/* Veredicto */}
            {principal && (
              <Box sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: `${COLOR_GRAVEDAD[principal.gravedad]}.main`, bgcolor: 'action.hover' }}>
                <Chip size="small" color={COLOR_GRAVEDAD[principal.gravedad]} label={TEXTO_GRAVEDAD[principal.gravedad]} sx={{ mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{principal.titulo}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{principal.explicacion}</Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>Qué hacer: {principal.accion}</Typography>
              </Box>
            )}

            {/* Lo que no avisó */}
            {informe.silencios?.length > 0 && (
              <Alert severity="warning" icon={<NotificationsOffIcon />} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Esto pasó sin que nadie se enterara
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                  {informe.silencios.map((s: any, i: number) => (
                    <Typography key={i} component="li" variant="body2">
                      <strong>{s.que}.</strong> {s.detalle}
                    </Typography>
                  ))}
                </Box>
                {informe.notificacionesTraccar?.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Notificaciones que hoy existen en Traccar:{' '}
                    {informe.notificacionesTraccar.map((n: any) => `${n.tipo} (${n.canal || 'sin canal'})`).join(' · ')}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Resumen */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
              <Dato titulo="Último reporte" valor={informe.equipo.ultimoReporteRD || '—'}
                pie={informe.equipo.horasSinReportar != null ? `hace ${informe.equipo.horasSinReportar} h` : undefined} />
              <Dato titulo="Corriente del vehículo"
                valor={informe.energia.voltajeActual != null ? `${informe.energia.voltajeActual} V` : '—'}
                pie={informe.energia.voltajeActual === 0 ? 'desconectado' : undefined} />
              <Dato titulo="Batería interna"
                valor={informe.energia.respaldoActual != null ? `${informe.energia.respaldoActual} V` : '—'} />
              <Dato titulo="SIM" valor={informe.sim?.estadoSimbase || 'sin vincular'}
                pie={informe.sim?.sesion || undefined} />
              <Dato titulo="Última sesión de datos" valor={informe.sim?.ultimaSesion?.finRD || '—'} />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {informe.zonaHoraria}
            </Typography>

            {/* Acciones */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Acciones</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {informe.accionesDisponibles.map((a: any) => (
                <Tooltip key={a.clave} title={a.descripcion || ''}>
                  <span>
                    <Button size="small" variant="outlined" disabled={!!ejecutando}
                      color={a.clave === 'deshabilitar_sim' || a.peligrosa ? 'error' : 'primary'}
                      onClick={() => setConfirmar(a)}>
                      {ejecutando === a.clave ? <CircularProgress size={16} /> : a.etiqueta}
                    </Button>
                  </span>
                </Tooltip>
              ))}
            </Box>

            {/* Hallazgos */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Todo lo que encontré ({informe.hallazgos.length})
            </Typography>
            {informe.hallazgos.map((h: any, i: number) => (
              <Accordion key={h.clave} defaultExpanded={i === 0} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" color={COLOR_GRAVEDAD[h.gravedad]} label={TEXTO_GRAVEDAD[h.gravedad]} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.titulo}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ mb: 1 }}>{h.explicacion}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>Evidencia</Typography>
                  <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2.5 }}>
                    {h.evidencia.map((e: string, j: number) => (
                      <Typography key={j} component="li" variant="body2" color="text.secondary">{e}</Typography>
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Qué hacer: {h.accion}</Typography>
                  {h.acciones?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {h.acciones.map((a: any) => (
                        <Button key={a.clave} size="small" variant="contained" disabled={!!ejecutando}
                          onClick={() => setConfirmar(a)}>{a.etiqueta}</Button>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Energía */}
            {informe.energia.serie?.length > 1 && (
              <Accordion disableGutters sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Corriente y batería ({informe.energia.cortes.length} corte{informe.energia.cortes.length === 1 ? '' : 's'} en {informe.ventanaDias} días)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <GraficaEnergia serie={informe.energia.serie} />
                  {informe.energia.cortes.length > 0 && (
                    <TableContainer sx={{ mt: 2, overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Se cortó</TableCell>
                            <TableCell>Volvió</TableCell>
                            <TableCell align="right">Duró</TableCell>
                            <TableCell align="right">Batería interna</TableCell>
                            <TableCell align="right">Descarga</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {informe.energia.cortes.map((c: any, i: number) => (
                            <TableRow key={i}>
                              <TableCell>{c.desdeRD}</TableCell>
                              <TableCell>{c.abierto ? <Chip size="small" color="error" label="sigue sin corriente" /> : c.hastaRD}</TableCell>
                              <TableCell align="right">{c.horas != null ? `${c.horas} h` : '—'}</TableCell>
                              <TableCell align="right">
                                {c.respaldoInicio != null ? `${c.respaldoInicio} → ${c.respaldoFin} V` : '—'}
                              </TableCell>
                              <TableCell align="right">{c.caidaPorHora != null ? `${c.caidaPorHora} V/h` : '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Dónde está, según quién */}
            {informe.contrasteUbicacion && (
              <Accordion disableGutters defaultExpanded={!informe.equipo.horasSinReportar || informe.equipo.horasSinReportar > 2}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Dónde está, según cada fuente</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {informe.contrasteUbicacion.desfaseHoras > 2 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {informe.contrasteUbicacion.advertencia}
                    </Alert>
                  )}
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Fuente</TableCell>
                          <TableCell>Coordenadas</TableCell>
                          <TableCell>De cuándo</TableCell>
                          <TableCell align="right">Margen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {informe.contrasteUbicacion.puntos.map((p: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.fuente}</Typography>
                              <Typography variant="caption" color="text.secondary">{p.detalle}</Typography>
                            </TableCell>
                            <TableCell>
                              <Link href={p.mapa} target="_blank" rel="noopener" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {p.lat}, {p.lon}
                              </Link>
                            </TableCell>
                            <TableCell>{p.cuandoRD || '—'}</TableCell>
                            <TableCell align="right">
                              ±{p.precisionM >= 1000 ? `${(p.precisionM / 1000).toFixed(1)} km` : `${p.precisionM} m`}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {informe.contrasteUbicacion.distancias?.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>Qué tan lejos están entre sí</Typography>
                      {informe.contrasteUbicacion.distancias.map((d: any, i: number) => (
                        <Typography key={i} variant="body2" color="text.secondary">
                          {d.entre}: <strong>{d.metros >= 1000 ? `${(d.metros / 1000).toFixed(1)} km` : `${d.metros} m`}</strong>
                        </Typography>
                      ))}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        La antena sirve para saber la zona de la ciudad, no la calle: su margen es de kilómetros.
                        Si cae cerca de donde el vehículo pasa las noches, es señal de que no se ha movido de su área.
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Antenas */}
            {informe.antenas?.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Antenas a las que se conectó ({informe.antenas.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Cuándo</TableCell>
                          <TableCell>Operador</TableCell>
                          <TableCell>Tecnología</TableCell>
                          <TableCell>Celda / LAC</TableCell>
                          <TableCell>Señal</TableCell>
                          <TableCell>Según</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {informe.antenas.map((a: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{a.cuandoRD}</TableCell>
                            <TableCell>{a.operador}{a.pais ? ` (${a.pais})` : ''}</TableCell>
                            <TableCell>{a.tecnologia || '—'}</TableCell>
                            <TableCell>{a.celda} / {a.lac}</TableCell>
                            <TableCell>{a.señal || '—'}</TableCell>
                            <TableCell><Typography variant="caption">{a.fuente}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Sesiones de datos */}
            {informe.sim?.sesiones?.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Sesiones de datos de la SIM ({informe.sim.sesiones.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Abrió</TableCell>
                          <TableCell>Cerró</TableCell>
                          <TableCell align="right">Datos</TableCell>
                          <TableCell>Red</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {informe.sim.sesiones.map((s: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{s.inicioRD}</TableCell>
                            <TableCell>{s.finRD}</TableCell>
                            <TableCell align="right">{s.kb != null ? `${s.kb} KB` : '—'}</TableCell>
                            <TableCell>{s.red}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            {/* SMS: la tercera vía para hablar con el equipo */}
            {informe.sms?.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    SMS con el equipo ({informe.sms.length})
                    {informe.sms.some((m: any) => m.esRespuesta) && (
                      <Chip size="small" color="success" label="contestó" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Los SMS viajan por la red de señalización, no por el APN, así que llegan aunque los
                    datos estén rotos. Ojo con el estado: <strong>sent</strong> solo significa que la red
                    lo aceptó; <strong>delivered</strong> que llegó al aparato.
                  </Typography>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Cuándo</TableCell>
                          <TableCell>Dirección</TableCell>
                          <TableCell>Mensaje</TableCell>
                          <TableCell>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {informe.sms.map((m: any, i: number) => (
                          <TableRow key={i} sx={m.esRespuesta ? { bgcolor: 'success.light', opacity: 0.9 } : undefined}>
                            <TableCell>{m.cuandoRD}</TableCell>
                            <TableCell>{m.direccion}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{m.mensaje}</TableCell>
                            <TableCell>{m.estado}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Historial de acciones sobre la SIM */}
            {informe.historialSim?.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Qué le hemos hecho a esta SIM ({informe.historialSim.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Activaciones, desactivaciones y resets hechos desde el portal. A veces la causa de
                    la avería está aquí y no en la calle.
                  </Typography>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Cuándo</TableCell>
                          <TableCell>Qué</TableCell>
                          <TableCell>Motivo</TableCell>
                          <TableCell>Quién</TableCell>
                          <TableCell>Vencimiento en ese momento</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {informe.historialSim.map((a: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{a.cuandoRD}</TableCell>
                            <TableCell>
                              <Chip size="small" label={a.accion}
                                color={a.accion === 'deactivate' ? 'error' : a.accion === 'activate' ? 'success' : 'default'} />
                            </TableCell>
                            <TableCell>{a.motivo || '—'}</TableCell>
                            <TableCell>{a.quien}</TableCell>
                            <TableCell>{a.vencimientoEnEseMomento || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Línea de tiempo */}
            {informe.lineaDeTiempo?.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Línea de tiempo cruzada ({informe.lineaDeTiempo.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Las tres fuentes mezcladas en la misma hora. Sirve para ver qué pasó primero.
                  </Typography>
                  <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
                    {informe.lineaDeTiempo.map((e: any, i: number) => (
                      <Box key={i} sx={{
                        display: 'flex', gap: 1.5, py: 0.6, alignItems: 'baseline',
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}>
                        <Typography variant="caption" sx={{ minWidth: 150, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                          {e.cuandoRD}
                        </Typography>
                        <Chip size="small" label={NOMBRE_FUENTE[e.fuente] || e.fuente}
                          sx={{ bgcolor: COLOR_FUENTE[e.fuente], color: '#fff', height: 18, fontSize: 10 }} />
                        <Typography variant="body2" sx={{ fontWeight: e.importante ? 600 : 400 }}>{e.texto}</Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

        <Dialog open={!!confirmar} onClose={() => setConfirmar(null)}>
          <DialogTitle>{confirmar?.etiqueta}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmar?.descripcion || 'Esta acción se ejecuta sobre la SIM real.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button variant="contained" onClick={() => confirmar && ejecutar(confirmar.clave)} disabled={!!ejecutando}>
              Ejecutar
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const Dato: React.FC<{ titulo: string; valor: string; pie?: string }> = ({ titulo, valor, pie }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{titulo}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{valor}</Typography>
    {pie && <Typography variant="caption" color="text.secondary">{pie}</Typography>}
  </Box>
);

export default DiagnosticoDispositivo;
